import React, { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import ExternalSourcePicker from '../../../../../external-sources/components/ExternalSourcePicker';
import { LinkBadge, LinkRow } from './ExternalLinkParts';
import ManualIdFields from './ManualIdFields';
import {
  EMPTY_LINKS,
  SOURCE_FIELDS,
  linksToSave,
  sameLinks,
  saveProblem,
  savedLinksOf,
  savedMessage,
} from './externalLinks';
import { persistLinks } from './persistLinks';
import { getExternalId } from '../../../../../external-sources/utils/externalIds';

const SessionExternalLinksCard = ({ session, onSaved, onNotify }) => {
  const { id: routeSessionId } = useParams();
  const pickerRef = useRef(null);
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [linkSource, setLinkSource] = useState('WordPress');
  const [draft, setDraft] = useState(EMPTY_LINKS);
  const [showManualIds, setShowManualIds] = useState(false);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [activePickerEventId, setActivePickerEventId] = useState('');
  const [pickerEditKey, setPickerEditKey] = useState(0);

  const saved = savedLinksOf(session);
  const eventId = getExternalId(session, 'event_id', 'eventId');
  const linked = {
    WordPress: Boolean(saved.eventWpId || saved.wpSessionId),
    Faicon: Boolean(saved.eventFaiconId || saved.faiconSessionId),
  };

  const setDraftField = (field, value) => {
    setDraft((prev) => (prev[field] === value ? prev : { ...prev, [field]: value }));
  };

  useEffect(() => {
    setExpanded(false);
    setEditing(false);
    setShowManualIds(false);
    setLinkSource('WordPress');
    setPickerLoading(false);
    setActivePickerEventId('');
  }, [routeSessionId]);

  useEffect(() => {
    if (!session || editing) return;
    const next = savedLinksOf(session);
    setDraft((prev) => (sameLinks(prev, next) ? prev : next));
  }, [session, editing, saved.wpSessionId, saved.faiconSessionId, saved.eventWpId, saved.eventFaiconId]);

  const reopenPicker = () => {
    setActivePickerEventId('');
    setPickerEditKey((prev) => prev + 1);
  };

  const startEditing = () => {
    setExpanded(true);
    const hasWp = Boolean(draft.eventWpId || draft.wpSessionId);
    const hasFaicon = Boolean(draft.eventFaiconId || draft.faiconSessionId);
    setLinkSource(!hasWp && hasFaicon ? 'Faicon' : 'WordPress');
    reopenPicker();
    setEditing(true);
  };

  const handleCancel = () => {
    setDraft(savedLinksOf(session));
    setShowManualIds(false);
    setLinkSource('WordPress');
    reopenPicker();
    setEditing(false);
  };

  const handlePickerSelection = (selection) => {
    const fields = SOURCE_FIELDS[selection.source];
    if (fields) {
      setDraftField(fields.event, selection.eventId || '');
      setDraftField(fields.session, selection.sessionId || '');
    }
    if (selection.source === linkSource) {
      setActivePickerEventId(selection.eventId || '');
    }
  };

  const handleSave = async () => {
    if (!session?.id) return;

    const picker = pickerRef.current?.getSelection?.();
    const values = linksToSave(draft, linkSource, picker);
    const problem = saveProblem({ values, linkSource, picker, pickerLoading, showManualIds });
    if (problem) {
      onNotify?.({ type: 'error', ...problem });
      return;
    }

    setSaving(true);
    try {
      const result = await persistLinks(session.id, eventId, values);
      const { event, session: sessionField } = SOURCE_FIELDS[linkSource];
      setDraft((prev) => ({ ...prev, [event]: values[event], [sessionField]: values[sessionField] }));
      setEditing(false);
      if (onSaved) await onSaved();
      onNotify?.({ type: 'success', title: 'External links saved', message: savedMessage(result) });
    } catch (err) {
      onNotify?.({
        type: 'error',
        title: 'Failed to save links',
        message: err.message || 'Could not update external links.',
      });
    } finally {
      setSaving(false);
    }
  };

  const activeFields = SOURCE_FIELDS[linkSource];
  const activeTabHasEvent = Boolean(draft[activeFields.event] || activePickerEventId);
  const shown = editing ? draft : saved;
  const showWpSessionBlock = editing ? Boolean(draft.eventWpId || saved.eventWpId) : linked.WordPress;
  const showFaiconSessionBlock = editing ? Boolean(draft.eventFaiconId || saved.eventFaiconId) : linked.Faicon;

  return (
    <div className={`session-detail-card session-external-links-accordion ${expanded ? 'is-expanded' : ''}`}>
      <button
        type="button"
        className="session-external-links-toggle"
        onClick={() => setExpanded((prev) => !prev)}
        aria-expanded={expanded}
      >
        <span className="session-external-links-toggle-text">
          <strong>External links</strong>
          <span className="session-external-links-summary-row">
            <LinkBadge label="WordPress" linked={linked.WordPress} />
            <LinkBadge label="Faicon" linked={linked.Faicon} />
            {!linked.WordPress && !linked.Faicon && (
              <span className="session-external-links-summary">Not linked</span>
            )}
          </span>
        </span>
        <ChevronDownIcon className={`session-external-links-chevron ${expanded ? 'is-open' : ''}`} />
      </button>

      <div className="session-external-links-body">
        <div className="session-external-links-body-inner">
          {!editing && (
            <div className="session-external-links-toolbar">
              <button
                type="button"
                className="session-side-link"
                onClick={startEditing}
              >
                Edit links
              </button>
            </div>
          )}

          <div className="session-kv-list session-external-links-events">
            <LinkRow label="Event WordPress ID" value={shown.eventWpId} />
            <LinkRow label="Event Faicon ID" value={shown.eventFaiconId} />
          </div>

          {(showWpSessionBlock || showFaiconSessionBlock) && (
            <div className="session-kv-list session-external-links-sessions">
              {showWpSessionBlock && <LinkRow label="WordPress session" value={shown.wpSessionId} />}
              {showFaiconSessionBlock && <LinkRow label="Faicon session" value={shown.faiconSessionId} />}
            </div>
          )}

          {editing && expanded && (
            <div className="session-external-links-edit-panel">
              <p className="session-side-note session-external-links-note">
                Link the event first, then pick the session used when publishing.
              </p>

              <div className="session-external-links-form">
                <ExternalSourcePicker
                  ref={pickerRef}
                  key={pickerEditKey}
                  mode="session"
                  source={linkSource}
                  onSourceChange={setLinkSource}
                  onSelectionChange={handlePickerSelection}
                  onLoadingChange={setPickerLoading}
                  linkedEventId={draft[activeFields.event]}
                  linkedSessionId={draft[activeFields.session]}
                  showSessionFields={activeTabHasEvent}
                  disabled={saving}
                />

                {!activeTabHasEvent && (
                  <p className="session-side-note session-external-links-note">
                    Select a {linkSource} event to unlock day and session fields.
                  </p>
                )}

                <button
                  type="button"
                  className="session-side-link"
                  onClick={() => setShowManualIds((prev) => !prev)}
                >
                  {showManualIds ? 'Hide manual IDs' : 'Enter IDs manually'}
                </button>

                {showManualIds && (
                  <ManualIdFields
                    draft={draft}
                    onChange={setDraftField}
                    saving={saving}
                    hasEvent={Boolean(eventId)}
                  />
                )}

                <div className="session-side-actions">
                  <button
                    type="button"
                    className="session-btn session-btn-secondary"
                    onClick={handleSave}
                    disabled={saving || pickerLoading}
                  >
                    {saving ? 'Saving...' : pickerLoading ? 'Loading...' : 'Save links'}
                  </button>
                  <button
                    type="button"
                    className="session-btn session-btn-ghost"
                    onClick={handleCancel}
                    disabled={saving}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {eventId && !editing && (
            <p className="session-external-links-footnote">
              You can also edit event metadata on the{' '}
              <Link to={`/events/${eventId}`}>event page</Link>.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default SessionExternalLinksCard;
